import { compact, uniq } from 'lodash/fp';
import { Reducer, useEffect, useMemo, useReducer } from 'react';
import { useDispatch } from 'react-redux';

import { getAllModuleSteps } from 'dashboard/lib/workflows/steps-config-operations';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { workflowBlockEndpoints } from 'dashboard/redux/api/workflow-block';
import { DashboardThunkDispatch } from 'dashboard/redux/types';

// TODO: generalize the part of batching singular queries as a reusable hook :)
const useModuleToBlockStepsConfigQuery = (tree: WorkflowStep[]) => {
  const moduleSteps = useMemo(() => getAllModuleSteps(tree), [tree]);
  const blockIds = useMemo(
    () => compact(uniq(moduleSteps.map((ms) => ms['child-workflow-block-id']))),
    [moduleSteps],
  );
  const [blocks, setBlock] = useReducer<
    Reducer<Record<string, WorkflowStep[]>, [string, WorkflowStep[]]>
  >((current, [blockId, stepsConfig]) => ({ ...current, [blockId]: stepsConfig }), {});
  const dispatch = useDispatch<DashboardThunkDispatch>();

  useEffect(() => {
    let active = true;
    const queryThunks = blockIds
      .map((blockId) => workflowBlockEndpoints.getWorkflowBlock.initiate(blockId))
      .map((action) => dispatch(action));

    queryThunks.forEach((queryThunk) =>
      queryThunk.unwrap().then((block) => {
        if (active) setBlock([block.data.id, block.data.attributes['steps-config']]);
      }),
    );

    return () => {
      active = false;
      queryThunks.forEach((queryThunk) => queryThunk.unsubscribe()); // Free up query from keeping cache
    };
  }, [blockIds, dispatch]);

  const moduleToStepsConfig = useMemo(
    () =>
      moduleSteps.reduce<Record<string, WorkflowStep[]>>(
        (moduleToSteps, moduleStep) => ({
          ...moduleToSteps,
          ...(blocks[moduleStep['child-workflow-block-id']]
            ? { [moduleStep.token]: blocks[moduleStep['child-workflow-block-id']] }
            : {}),
        }),
        {},
      ),
    [blocks, moduleSteps],
  );

  return moduleToStepsConfig;
};

export default useModuleToBlockStepsConfigQuery;
